import { useTranslation } from "react-i18next";
import type { MemberCheck, MemberCheckStatus, ReleaseChecks } from "../../../api/knowledgeData/releaseCheckDecoders";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import type { ChipTone } from "../../../ui/styles/chipStyles";
import CheckResultList from "../../reviews/checks/CheckResultList";

const STATUS_TONE: Readonly<Record<MemberCheckStatus, ChipTone>> = {
  pass: "success",
  fail: "danger",
  cannot_run: "warning",
  not_determined: "warning",
  no_instance_data: "info",
};

/**
 * A release's check report (knowledge-data.md, D4): the one model version every member was
 * checked against, each member's result with its reasons (its terms, and the shapes
 * violations its triples are involved in), then the shapes and the consistency checks of
 * the merged release, the latter with exactly the rules that ran. The same view for a stored
 * version and for a refused one.
 */
export default function ReleaseChecksPanel({ checks }: { checks: ReleaseChecks }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 text-sm">
      <p>
        {t("data.releaseChecks.against")}{" "}
        <span className="break-all font-mono text-xs">{checks.contentVersion}</span>
      </p>
      {!checks.attributionComplete ? <p className="app-text-muted text-xs">{t("data.releaseChecks.attributionPartial")}</p> : null}
      {checks.shortened ? <p className="app-text-muted text-xs">{t("data.releaseChecks.shortened")}</p> : null}
      <h5 className="app-subsection-title text-xs">{t("data.releaseChecks.members")}</h5>
      <ul className="space-y-2">
        {checks.members.map((member) => (
          <MemberRow key={member.extraction} member={member} contentVersion={checks.contentVersion} />
        ))}
      </ul>
      <h5 className="app-subsection-title text-xs">{t("data.releaseChecks.mergedShapes")}</h5>
      {checks.shapes.length === 0 ? <EmptyState size="small">{t("data.releaseChecks.noShapes")}</EmptyState> : <CheckResultList results={checks.shapes} />}
      <h5 className="app-subsection-title text-xs">{t("data.releaseChecks.merged")}</h5>
      <CheckResultList results={checks.consistency} />
      <p className="app-text-muted text-xs">{t("data.releaseChecks.engine", { engine: checks.engine })}</p>
    </div>
  );
}

function MemberRow({ member, contentVersion }: { member: MemberCheck; contentVersion: string }) {
  const { t } = useTranslation();
  const checked = member.status !== "no_instance_data";
  const older = checked && member.writtenAgainst !== null && member.writtenAgainst !== contentVersion;
  const unlisted = checked && member.status !== "pass" && member.status !== "not_determined" && member.problems.length === 0;
  return (
    <li className="flex flex-wrap items-start gap-2">
      <Chip tone={STATUS_TONE[member.status]}>{t(`data.releaseChecks.status.${member.status}`)}</Chip>
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block break-all font-mono text-xs">{member.extraction}</span>
        {older ? <span className="app-text-muted block break-all text-xs">{t("data.releaseChecks.older", { version: member.writtenAgainst })}</span> : null}
        {member.status === "no_instance_data" ? <span className="app-text-muted block text-xs">{t("data.releaseChecks.noInstanceData")}</span> : null}
        {member.terms !== null && member.shapes !== null ? (
          <span className="app-text-muted block text-xs">
            {t("data.releaseChecks.outcomes", { terms: t(`data.releaseChecks.status.${member.terms}`), shapes: t(`data.releaseChecks.status.${member.shapes}`) })}
          </span>
        ) : null}
        {member.shapes === "not_determined" ? <span className="app-text-muted block text-xs">{t("data.releaseChecks.notDetermined")}</span> : null}
        {member.problems.length > 0 ? (
          <ul className="list-disc pl-5 text-xs">
            {member.problems.map((problem, index) => (
              // The checker may give the same reason for several statements.
              <li key={`${String(index)}:${problem.reason}`}>
                {problem.statement === null ? problem.reason : t("data.releaseChecks.statementProblem", { number: problem.statement + 1, reason: problem.reason })}
              </li>
            ))}
          </ul>
        ) : null}
        {unlisted ? <span className="app-text-muted block text-xs">{t("data.releaseChecks.unlisted")}</span> : null}
      </span>
    </li>
  );
}
